import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import {
  hardReset,
  reportProcessData,
  reportProcessList,
  reportDetailsGet,
  clearBroadcastBufferReport
} from '../../../store/slices/tenantSlices/realtimeReportSlice';

import HardResetConfirmationModal from './HardResetConfirmationModal';
import AdminHeader from '../../../components/header/AdminHeader';
import FormSelect from '../../../components/form/FormSelect';
import { refreshTimeOptions } from '../../../utils/common';
import ProcessDetails from './ProcessDetails';
import BroadcastReportCount from './BroadcastReportCount';
import BroadcastReportList from './BroadcastReportList';
import ClearBufferConfirmationModal from './ClearBufferConfirmationModal';
import { filterRowsByStatus, getBroadcastCard } from './broadcastStatus';

// Every /voicebroadcast_realtime endpoint has always taken a single `selected_process_id`.
// Multi-select sends a comma-separated list; a single selection still sends the bare id,
// so nothing regresses for one process if the backend has not shipped multi-id support
// yet. If the backend wants a different shape, this is the only place to change.
const toProcessParam = (ids) => (ids?.length ? ids.join(',') : undefined);

const BroadcastReport = () => {
  const dispatch = useDispatch();

  // Replaces the old processId + hyperProcessId pair. `hyperProcessId` only ever existed
  // to override the dropdown with one process; with an array, drilling in is just a
  // selection of one.
  const [selectedProcessIds, setSelectedProcessIds] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [refreshTime, setRefreshTime] = useState(5);
  const [processOptions, setProcessOptions] = useState([]);
  const [proData, setProData] = useState([]);
  const [countData, setCountData] = useState([]);
  const [listData, setListData] = useState([]);
  const [hardResetModalOpen, setHardResetModalOpen] = useState(false);
  const [bufferClearModalOpen, setBufferClearModalOpen] = useState(false);

  const getProcessList = async () => {
    try {
      const response = await dispatch(reportProcessList()).unwrap();
      const options =
        response && response.data.length > 0
          ? response.data.map((item) => ({ label: item.process_name, value: item.process_id }))
          : [];
      // No hand-rolled "All" option: FormSelect injects its own "Select All" when isMulti,
      // and an empty selection already means every process to the API.
      setProcessOptions(options);
    } catch (error) {
      console.log('error', error);
    }
  };

  const getProcessData = async (process_id) => {
    try {
      const payload = {
        selected_process_id: process_id || undefined
      };
      const response = await dispatch(reportProcessData(payload)).unwrap();
      if (response && response?.data && response?.data?.length > 0) {
        setProData(response.data);
      } else {
        setProData([]);
      }
    } catch (error) {
      setProData([]);
      console.log('error', error);
    }
  };

  const onBufferClear = async () => {
    try {
      const response = await dispatch(
        clearBroadcastBufferReport({ selected_process_id: toProcessParam(selectedProcessIds) })
      ).unwrap();
      toast.success(response?.message);
      setBufferClearModalOpen(false);
    } catch (error) {
      console.log('error', error);
    }
  };

  const getReportDetailsData = async (process_id) => {
    try {
      const payload = {
        selected_process_id: process_id || undefined
      };
      const response = await dispatch(reportDetailsGet(payload)).unwrap();
      if (response && response?.count_data && response?.count_data?.length > 0) {
        setCountData(response.count_data);
      } else {
        setCountData([]);
      }
      if (response && response?.data && response?.data?.length > 0) {
        setListData(response.data);
      } else {
        setListData([]);
      }
    } catch (error) {
      setCountData([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getProcessList();
  }, []);

  useEffect(() => {
    const fetchData = () => {
      const id = toProcessParam(selectedProcessIds);
      getProcessData(id);
      getReportDetailsData(id);
    };

    fetchData();

    if (refreshTime) {
      const intervalId = setInterval(fetchData, refreshTime * 1000);

      return () => clearInterval(intervalId);
    }
  }, [refreshTime, selectedProcessIds]);

  // The status filter is held here, not inside BroadcastReportCount, so the poll above can
  // replace listData every few seconds without dropping the selection.
  const visibleListData = useMemo(
    () => filterRowsByStatus(listData, selectedStatus),
    [listData, selectedStatus]
  );

  const listEmptyMessage = selectedStatus
    ? `No calls in ${getBroadcastCard(selectedStatus)?.title}`
    : 'No data available';

  const onHardReset = async () => {
    try {
      const response = await dispatch(hardReset()).unwrap();
      toast.success(response?.message);
      setHardResetModalOpen(false);
    } catch (error) {
      console.log('error', error);
    }
  };

  return (
    <Fragment>
      <AdminHeader title="Voice Broadcast Realtime Report" />
      <Row>
        <Col lg="2" md="2" className="px-2">
          <Card className="mb-3 px-2 py-1">
            <Row>
              <Col lg="12" md="12">
                <FormSelect
                  isShowLabel={false}
                  isMulti
                  name="processId"
                  value={selectedProcessIds}
                  placeholder="All Processes"
                  options={processOptions}
                  onChange={(e) => setSelectedProcessIds(e.target.value || [])}
                />
              </Col>
              {/* <Col lg="12" md="12">
                <Button
                  color="primary"
                  className="px-4 w-100"
                  type="submit"
                  onClick={() => setHardResetModalOpen(true)}>
                  Hard Reset
                </Button>
              </Col> */}
            </Row>
          </Card>
          <Card className="m-0 broadcast-process-details">
            {/* Clicking a process drills into it: the selection narrows to that one. */}
            <ProcessDetails
              data={proData}
              setHyperProcessId={(id) => setSelectedProcessIds([id])}
              activeIds={selectedProcessIds}
            />
          </Card>
        </Col>
        <Col lg="10" md="10">
          <Row>
            <Col lg="9" md="9">
              <Card className="p-2 m-0">
                <BroadcastReportCount
                  data={countData}
                  selectedStatus={selectedStatus}
                  onSelectStatus={setSelectedStatus}
                />
              </Card>
            </Col>
            <Col lg="3" md="3" className="px-2">
              <Card className="mb-3 px-2 py-2">
                <Row>
                  <Col lg="12" md="12">
                    <FormSelect
                      isShowLabel={false}
                      name="refreshTime"
                      value={refreshTime}
                      placeholder="Select Refresh"
                      options={refreshTimeOptions}
                      onChange={(e) => setRefreshTime(e.target.value)}
                    />
                  </Col>
                  <Col lg="12" md="12">
                    <Button
                      color="primary"
                      className="px-4 w-100"
                      type="submit"
                      onClick={() => setBufferClearModalOpen(true)}>
                      Clear Buffer
                    </Button>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col lg="12" md="12">
              <Card className="p-2 m-0">
                <BroadcastReportList data={visibleListData} emptyMessage={listEmptyMessage} />
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
      <ClearBufferConfirmationModal
        isOpen={bufferClearModalOpen}
        onClearClick={() => onBufferClear()}
        onCloseClick={() => setBufferClearModalOpen(false)}
      />
      <HardResetConfirmationModal
        isOpen={hardResetModalOpen}
        onResetClick={() => onHardReset()}
        onCloseClick={() => setHardResetModalOpen(false)}
      />
    </Fragment>
  );
};

export default BroadcastReport;
